'use client';

import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../icons';
import { Caption } from '../Caption/Caption';
import { AnimatePresence, motion } from 'motion/react';
import { InputText } from '../InputText/InputText';
import './InputSelect.css';

export interface SelectOption<TValue> {
  value: TValue;
  label: string;
  description?: string;
  // Renders a visual separator beneath this option to group it apart from the rest.
  // Suppressed while a search query is active so the list reads as a flat result set.
  dividerAfter?: boolean;
}

export interface InputSelectProps<TValue> {
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  caption?: React.ReactNode;
  multiple?: boolean; // Not currently supported by custom popup
  searchable?: boolean; // Enables a client-side search field inside the dropdown
  onSearchChange?: (query: string) => void; // Server search; supplied options are already filtered.
  onOpenChange?: (open: boolean) => void;
  searchPlaceholder?: string;
  emptyContent?: React.ReactNode;
  menuFooter?: React.ReactNode;
  selectedLabel?: string;
  options: SelectOption<TValue>[];
  value: TValue | null;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  leadingVisual?: React.ReactNode;
  trailingVisual?: React.ReactNode;
  message?: string;
  messageType?: '' | 'error' | 'success';
  id?: string;
  size?: 'sm' | 'md' | 'lg';
  // Portal rendering
  portal?: boolean; // default true
  portalContainer?: HTMLElement | (() => HTMLElement | null);
  portalZIndex?: React.CSSProperties['zIndex'];
  positionStrategy?: 'fixed' | 'absolute';
}

export const InputSelect = React.memo(function InputSelect<TValue>({
  label = 'Label',
  required = false,
  disabled = false,
  placeholder = '',
  caption,
  // multiple, // reserved
  searchable = false,
  onSearchChange,
  onOpenChange,
  searchPlaceholder = 'Search...',
  emptyContent = 'No results',
  menuFooter,
  selectedLabel,
  options,
  value,
  onChange,
  leadingVisual,
  trailingVisual,
  message,
  messageType = '',
  id,
  size = 'md',
  portal = true,
  portalContainer,
  portalZIndex = 'var(--z-index-overlay, 1000)',
  positionStrategy = 'fixed',
}: InputSelectProps<TValue>) {
  const reactId = useId();
  const selectId = id ?? `input-select-${reactId}`;
  const captionId = caption ? `${selectId}-caption` : undefined;
  const messageId = message ? `${selectId}-message` : undefined;
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const portalContentRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastSelectedSValue, setLastSelectedSValue] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [portalPosition, setPortalPosition] = useState<{
    left: number;
    top: number;
    minWidth: number;
  } | null>(null);
  const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');
  const [menuHeight, setMenuHeight] = useState<number | null>(null);

  useEffect(() => setMounted(true), []);

  // Detect native HTML Popover API support. When supported, the menu is promoted
  // to the browser's top layer, which renders above modal <dialog> elements and
  // bypasses ancestor overflow clipping entirely.
  const supportsPopover =
    typeof window !== 'undefined' &&
    typeof HTMLElement !== 'undefined' &&
    'showPopover' in HTMLElement.prototype;

  // Resolve the portal container. If the trigger lives inside an open modal <dialog>,
  // we portal into that dialog so the menu is a DOM descendant of the modal subtree.
  // This is essential: a modal <dialog> blocks all nodes that are NOT its descendants
  // from receiving pointer/wheel events (the "blocked by modal" state). Even when the
  // popover is promoted to the top layer above the dialog, if its DOM home is outside
  // the dialog the browser still treats it as blocked, so clicks pass through and
  // scrolling fails. Portaling inside the dialog avoids that, while popover still
  // handles top-layer painting + escaping overflow clipping.
  const resolvePortalContainer = useCallback((): HTMLElement | null => {
    const explicit =
      typeof portalContainer === 'function'
        ? (portalContainer() ?? null)
        : (portalContainer ?? null);
    if (explicit) return explicit;
    const trigger = triggerRef.current;
    if (trigger) {
      const dialogAncestor = trigger.closest('dialog[open]') as HTMLElement | null;
      if (dialogAncestor) return dialogAncestor;
    }
    return null;
  }, [portalContainer]);

  const items = useMemo(() => options.map((o) => ({ ...o, sValue: String(o.value) })), [options]);
  const isSearchEnabled = searchable || Boolean(onSearchChange) || items.length > 6;
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (onSearchChange || !isSearchEnabled || normalizedQuery.length === 0) return items;
    return items.filter(
      (o) =>
        o.label.toLowerCase().includes(normalizedQuery) ||
        o.sValue.toLowerCase().includes(normalizedQuery),
    );
  }, [isSearchEnabled, items, normalizedQuery, onSearchChange]);
  const selected = useMemo(
    () => (value == null ? null : (items.find((o) => o.sValue === String(value)) ?? null)),
    [items, value],
  );

  const handleValueChange = (newValue: string | null) => {
    const synthetic = {
      target: { value: newValue ?? '' },
    } as unknown as ChangeEvent<HTMLSelectElement>;
    onChange(synthetic);
  };

  const openDropdown = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    onOpenChange?.(true);
    setLastSelectedSValue(null);
    const currentIndexInFiltered = selected
      ? filteredItems.findIndex((i) => i.sValue === selected.sValue)
      : -1;
    setHighlightedIndex(currentIndexInFiltered);
    // Seed initial portal position so the menu appears immediately when portaled
    if (portal) {
      const triggerEl = triggerRef.current;
      if (triggerEl) {
        const rect = triggerEl.getBoundingClientRect();
        const gap = 4; // match CSS offset
        if (positionStrategy === 'fixed') {
          setPortalPosition({
            left: Math.max(0, Math.round(rect.left)),
            top: Math.round(rect.bottom + gap),
            minWidth: Math.max(0, Math.round(rect.width)),
          });
        } else {
          const containerEl: HTMLElement | null =
            typeof portalContainer === 'function'
              ? (portalContainer() as HTMLElement | null)
              : (portalContainer ?? null);
          const containerRect = containerEl?.getBoundingClientRect();
          const containerLeft = containerRect?.left ?? 0;
          const containerTop = containerRect?.top ?? 0;
          setPortalPosition({
            left: Math.max(0, Math.round(rect.left - containerLeft)),
            top: Math.round(rect.bottom - containerTop + gap),
            minWidth: Math.max(0, Math.round(rect.width)),
          });
        }
      }
    }
  }, [disabled, filteredItems, selected, portal, positionStrategy, portalContainer, onOpenChange]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
    setHighlightedIndex(-1);
    setSearchQuery('');
    onSearchChange?.('');
    // Restore focus to trigger after close
    setTimeout(() => {
      triggerRef.current?.focus();
    }, 0);
  }, [onOpenChange, onSearchChange]);

  // Close on outside click and Escape
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        (!portal ||
          (portal && portalContentRef.current && !portalContentRef.current.contains(target))) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        closeDropdown();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDropdown();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, closeDropdown, portal]);

  // Compute portal position relative to viewport (fixed) or container (absolute)
  useEffect(() => {
    if (!open || !portal) return;
    const updatePosition = () => {
      const triggerEl = triggerRef.current;
      if (!triggerEl) return;
      const rect = triggerEl.getBoundingClientRect();
      const gap = 4; // match CSS offset
      const minWidth = Math.max(0, Math.round(rect.width));
      const availableBelow = Math.max(0, window.innerHeight - rect.bottom - gap);
      const availableAbove = Math.max(0, rect.top - gap);
      const measuredHeight = menuHeight ?? 0;
      let nextPlacement: 'bottom' | 'top' = 'bottom';
      if (measuredHeight > 0) {
        if (availableBelow < measuredHeight && availableAbove > availableBelow) {
          nextPlacement = 'top';
        }
      } else {
        // Fallback heuristic before measurement
        nextPlacement = availableBelow >= availableAbove ? 'bottom' : 'top';
      }
      if (placement !== nextPlacement) setPlacement(nextPlacement);

      if (positionStrategy === 'fixed') {
        const top =
          nextPlacement === 'bottom'
            ? Math.round(rect.bottom + gap)
            : Math.round(rect.top - gap - (measuredHeight || Math.min(availableAbove, 320)));
        setPortalPosition({
          left: Math.max(0, Math.round(rect.left)),
          top: Math.max(0, top),
          minWidth,
        });
      } else {
        // 'absolute' relative to portal container
        const containerEl: HTMLElement | null =
          typeof portalContainer === 'function'
            ? (portalContainer() as HTMLElement | null)
            : (portalContainer ?? null);
        const containerRect = containerEl?.getBoundingClientRect();
        const containerLeft = containerRect?.left ?? 0;
        const containerTop = containerRect?.top ?? 0;
        const top =
          nextPlacement === 'bottom'
            ? Math.round(rect.bottom - containerTop + gap)
            : Math.round(
                rect.top - containerTop - gap - (measuredHeight || Math.min(availableAbove, 320)),
              );
        setPortalPosition({
          left: Math.max(0, Math.round(rect.left - containerLeft)),
          top: Math.max(0, top),
          minWidth,
        });
      }
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    // Reposition on any scroll (capture) to handle scrollable containers
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, portal, portalContainer, positionStrategy, menuHeight, placement]);

  // Promote the menu to the browser's top layer via the Popover API when supported.
  // This must run synchronously after mount (before paint) so the open animation
  // starts in the top layer and renders above any modal <dialog> / overflow ancestors.
  // Hiding is left to AnimatePresence: when `open` flips to false the exit animation
  // plays in the top layer, then unmount removes the popover automatically.
  useLayoutEffect(() => {
    if (!open || !portal || !supportsPopover) return;
    const el = portalContentRef.current as (HTMLDivElement & { showPopover?: () => void }) | null;
    if (!el || typeof el.showPopover !== 'function') return;
    try {
      // A modal can open above a still-exiting menu. Reopening must raise it again.
      if (el.matches(':popover-open')) el.hidePopover();
      el.showPopover();
    } catch {
      // Already shown or not connected yet — safe to ignore.
    }
  }, [open, portal, supportsPopover]);

  // Measure menu height when open to enable accurate flip positioning
  useEffect(() => {
    if (!open) return;
    const el = portalContentRef.current;
    if (!el) return;
    const measure = () => {
      const cs = window.getComputedStyle(el);
      const maxH = cs.maxHeight && cs.maxHeight !== 'none' ? parseFloat(cs.maxHeight) : Infinity;
      const natural = el.scrollHeight; // content height regardless of viewport clipping
      const desired = Math.min(natural, isFinite(maxH) ? maxH : natural);
      setMenuHeight(Math.max(0, Math.round(desired)));
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) {
        openDropdown();
      } else {
        setHighlightedIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
      }
    } else if (e.key === 'ArrowUp' && open) {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const onListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Search text needs spaces; footer buttons handle their own keyboard activation.
    if ((e.key === ' ' && e.target instanceof HTMLInputElement) ||
      (e.target instanceof Element && e.target.closest('button'))) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const opt = filteredItems[highlightedIndex];
      if (opt) {
        setLastSelectedSValue(opt.sValue);
        handleValueChange(opt.sValue);
        closeDropdown();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
    }
  };

  // Keep highlighted index within bounds when filter changes
  useEffect(() => {
    if (highlightedIndex >= filteredItems.length) {
      setHighlightedIndex(filteredItems.length - 1);
    }
  }, [filteredItems.length, highlightedIndex]);

  useEffect(() => {
    if (open && highlightedIndex >= 0) {
      document.getElementById(`${selectId}-option-${highlightedIndex}`)?.scrollIntoView({ block: 'nearest' });
    }
  }, [open, highlightedIndex, selectId]);

  // Auto-focus search input when opening and search is enabled
  useEffect(() => {
    if (open && isSearchEnabled) {
      // Defer to next tick so input is in DOM
      const id = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [open, isSearchEnabled]);

  // no-op: search key handling is bubbled to list container

  return (
    <div className="input-select" data-disabled={disabled} data-size={size}>
      <div>
        <span className="input-select__label" id={`${selectId}-label`}>
          {label}
          {required && <span className="input-select__required">*</span>}
        </span>
        {caption && (
          <Caption>
            <span id={captionId}>{caption}</span>
          </Caption>
        )}
      </div>

      <div className="input-select__dropdown" ref={dropdownRef}>
        <motion.button
          ref={triggerRef}
          type="button"
          className="input-select__trigger-button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${selectId}-label`}
          data-activity-label={label}
          aria-describedby={[captionId, messageId].filter(Boolean).join(' ') || undefined}
          aria-invalid={messageType === 'error' || undefined}
          onClick={() => (open ? closeDropdown() : openDropdown())}
          onKeyDown={onTriggerKeyDown}
          data-disabled={disabled}
          disabled={disabled}
          /* no scaling on hover/tap */
        >
          {leadingVisual}
          <span>{selectedLabel || (selected ? selected.label : placeholder || 'Select...')}</span>
          {trailingVisual}
          <Icon name="ChevronDown" className="input-select__arrow" />
        </motion.button>
        {(() => {
          const isTopPlacement = placement === 'top';
          const initialAnim = isTopPlacement
            ? { opacity: 0, transformOrigin: 'bottom left' }
            : { opacity: 0, scale: 0.98, transformOrigin: 'top left' };
          const animateAnim = isTopPlacement
            ? { opacity: 1, transformOrigin: 'bottom left' }
            : { opacity: 1, scale: 1, transformOrigin: 'top left' };
          const exitAnim = isTopPlacement
            ? { opacity: 0, transformOrigin: 'bottom left' }
            : { opacity: 0, scale: 0.98, transformOrigin: 'top left' };
          const menuNode = (
            <motion.div
              ref={portalContentRef as React.RefObject<HTMLDivElement>}
              className="input-select__content"
              data-placement={placement}
              initial={initialAnim}
              animate={animateAnim}
              exit={exitAnim}
              transition={{ duration: 0.14 }}
              // When supported, the Popover API promotes the menu to the browser's
              // top layer so it renders above modal <dialog> elements and is not
              // clipped by ancestor `overflow` containers.
              {...(portal && supportsPopover ? { popover: 'manual' } : {})}
              style={
                portal && portalPosition
                  ? ({
                      position: positionStrategy,
                      top: portalPosition.top,
                      left: portalPosition.left,
                      right: 'auto',
                      bottom: 'auto',
                      margin: 0,
                      minWidth: portalPosition.minWidth,
                      ...(onSearchChange ? { width: portalPosition.minWidth, maxWidth: 'calc(100vw - 16px)' } : {}),
                      zIndex: portalZIndex,
                      willChange: 'transform, opacity',
                    } as React.CSSProperties)
                  : ({ willChange: 'transform, opacity' } as React.CSSProperties)
              }
              onKeyDown={onListKeyDown}
              tabIndex={-1}
            >
              {isSearchEnabled && (
                <div className="input-select__search-wrapper">
                  <InputText
                    ref={searchInputRef}
                    value={searchQuery}
                    size="sm"
                    // The popover repositions on scroll/resize via state-driven
                    // top/left updates. Motion's layout animation would interpret
                    // each reposition as a layout change and animate the search
                    // input "into place", causing visible bouncing. Disable it.
                    disableLayoutAnimation
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchQuery(String(e.target.value));
                      onSearchChange?.(String(e.target.value));
                      setHighlightedIndex(-1);
                    }}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={`${selectId}-listbox`}
                    aria-activedescendant={highlightedIndex >= 0 ? `${selectId}-option-${highlightedIndex}` : undefined}
                    leadingVisual="Search"
                  />
                </div>
              )}

              <div
                role="listbox"
                id={`${selectId}-listbox`}
                aria-labelledby={`${selectId}-label`}
                className="input-select__list"
                data-search-enabled={isSearchEnabled || undefined}
              >
                {filteredItems.length === 0 && (
                  <div className="input-select__no-results" aria-live="polite">
                    {emptyContent}
                  </div>
                )}

                {filteredItems.map((opt, index) => {
                  const isSelected = selected?.sValue === opt.sValue;
                  const isHighlighted = highlightedIndex === index;
                  const showDivider =
                    opt.dividerAfter &&
                    normalizedQuery.length === 0 &&
                    index < filteredItems.length - 1;
                  return (
                    <React.Fragment key={opt.sValue}>
                    <motion.div
                      role="option"
                      id={`${selectId}-option-${index}`}
                      aria-selected={isSelected}
                      className="input-select__item"
                      data-highlighted={isHighlighted || undefined}
                      tabIndex={-1}
                      whileTap={{ scale: 0.98 }}
                      initial={false}
                      animate={
                        isSelected && lastSelectedSValue === opt.sValue
                          ? { scale: [1, 1.04, 1] }
                          : { scale: 1 }
                      }
                      transition={{ duration: 0.2, times: [0, 0.5, 1], ease: [0.22, 1, 0.36, 1] }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => {
                        setLastSelectedSValue(opt.sValue);
                        handleValueChange(opt.sValue);
                        closeDropdown();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setLastSelectedSValue(opt.sValue);
                          handleValueChange(opt.sValue);
                          closeDropdown();
                        }
                      }}
                    >
                      <span className="input-select__item-label">{opt.label}
                        {opt.description && <small className="input-select__item-description">{opt.description}</small>}
                      </span>
                      <AnimatePresence initial={false}>
                        {isSelected && (
                          <motion.span
                            className="input-select__item-check"
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.85 }}
                            transition={{ duration: 0.12 }}
                          >
                            <Icon name="Check" />
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </motion.div>
                    {showDivider && (
                      <div className="input-select__divider" role="presentation" />
                    )}
                    </React.Fragment>
                  );
                })}
              </div>
              {menuFooter && <div className="input-select__footer">{menuFooter}</div>}
            </motion.div>
          );

          if (portal && mounted) {
            const container = (resolvePortalContainer() ?? document.body) as HTMLElement;
            return createPortal(<AnimatePresence>{open && menuNode}</AnimatePresence>, container);
          }
          return <AnimatePresence>{open && menuNode}</AnimatePresence>;
        })()}
      </div>

      {message && (
        <span id={messageId} className="input-select__message" data-type={messageType}>
          {message}
        </span>
      )}
    </div>
  );
});

InputSelect.displayName = 'InputSelect';
